import React, { useMemo, createContext, useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Params } from 'data';
import { RestClient, Storage, buildUserRoleState, onLogout } from 'helpers';
import PropTypes from 'prop-types';

export const UserContext = createContext({});

const CONST_USER = 'user';
const CONST_USER_TOKEN = 'user_token';

const UserProvider = ({ children }) => {
  const storage = useMemo(() => new Storage(localStorage, 'eHadj-'), []);
  const [user, setUserState] = useState(() => storage.getData(CONST_USER));
  const token = storage.get(CONST_USER_TOKEN);
  const isConnected = !!token;
  const api = useMemo(() => new RestClient(Params.API_BASE_URL, token, onLogout), [token]);

  // UserProvider owns session state only.
  // The actual RBAC derivation lives in `src/helpers/userRoles.js`, where we:
  // 1. normalize the multi-organization payload,
  // 2. resolve the active organization and the role paired with it,
  // 3. expose the boolean flags used by pages and route guards.
  const roleState = useMemo(() => buildUserRoleState(user), [user]);

  const setCurrentUser = useCallback(
    (nextUser) => {
      // Keep React state as the primary source of truth so current-user updates
      // re-render immediately without waiting for a reload.
      setUserState(nextUser || null);

      if (nextUser) {
        storage.setData(CONST_USER, nextUser);
      }
    },
    [storage]
  );

  const refreshCurrentUser = useCallback(async () => {
    // We always re-fetch after session-sensitive mutations (for example org
    // switching) instead of inferring the next RBAC state locally.
    if (!token) {
      return null;
    }

    const { data } = await api.get('user/current/');
    setCurrentUser(data);
    return data;
  }, [api, setCurrentUser, token]);

  // Use react-query to handle the on-mount fetch instead of a bare useEffect.
  useQuery({
    queryKey: ['user', 'current'],
    queryFn: refreshCurrentUser,
    enabled: isConnected,
  });

  const memoValues = useMemo(
    () => ({
      user,
      ...roleState,
      currentUserEmail: user?.email,
      currentUserLastname: user?.last_name,
      currentUserFirstname: user?.first_name,
      isConnected,
      isOnboarded: user?.is_onboard,
      setCurrentUser,
      refreshCurrentUser,
    }),
    [isConnected, refreshCurrentUser, roleState, setCurrentUser, user]
  );

  return <UserContext.Provider value={memoValues}>{children}</UserContext.Provider>;
};

UserProvider.propTypes = {
  children: PropTypes.node,
};

export default UserProvider;
